import React, { forwardRef, useImperativeHandle, useRef } from 'react';

export type LogoHandle = {
  exportPNG: (width: number, height: number, filename?: string) => Promise<string>;
};

type LogoProps = {
  size?: number;
  withText?: boolean;
  text?: string;
  className?: string;
  rounded?: boolean;
  variant?: 'mark' | 'horizontal';
  width?: number;
  height?: number;
  fontFamily?: string;
};

const Logo = forwardRef<LogoHandle, LogoProps>(function Logo(
  {
    size = 128,
    withText = false,
    text = 'DevDriven',
    className = '',
    rounded = true,
    variant = 'mark',
    width,
    height,
    fontFamily = 'Segoe UI, Inter, Helvetica Neue, Arial, sans-serif'
  },
  ref
) {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useImperativeHandle(ref, () => ({
    exportPNG: async (width: number, height: number, filename = 'devdriven-logo.png') => {
      if (!svgRef.current) return '';
      const svg = svgRef.current.cloneNode(true) as SVGSVGElement;
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(height));
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svg);
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return dataUrl;
    }
  }));

  const cornerRadius = rounded ? 96 : 0;
  const showText = withText ? 1 : 0;
  const w = width ?? size;
  const h = height ?? size;

  return (
    <div
      className={`inline-flex items-center justify-center ${className}`}
      style={{ width: w, height: h }}
    >
      {variant === 'mark' ? (
        <svg
          ref={svgRef}
          viewBox="0 0 512 512"
          width={w}
          height={h}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#0ea5e9" />
            </linearGradient>
            <linearGradient id="shine" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
            <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="6" result="blur" />
            </filter>
          </defs>
          <rect x="32" y="32" width="448" height="448" rx={cornerRadius} fill="url(#bg)" />
          <rect x="32" y="32" width="448" height="448" rx={cornerRadius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
          <rect x="32" y="32" width="448" height="224" rx={cornerRadius} fill="url(#shine)" />
          <text
            x="256"
            y="256"
            fontSize="270"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(255,255,255,0.22)"
            fontFamily={fontFamily}
            transform="translate(6,6)"
          >
            D
          </text>
          <text
            x="256"
            y="256"
            fontSize="260"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#ffffff"
            fontFamily={fontFamily}
          >
            D
          </text>
          {showText ? (
            <text
              x="256"
              y="430"
              fontSize="52"
              fontWeight="700"
              textAnchor="middle"
              fill="#ffffff"
              fontFamily={fontFamily}
            >
              {text}
            </text>
          ) : null}
        </svg>
      ) : (
        <svg
          ref={svgRef}
          viewBox="0 0 1024 256"
          width={w}
          height={h}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="bgH" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#0ea5e9" />
            </linearGradient>
            <linearGradient id="shineH" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
            <filter id="softShadowH" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="5" result="blur" />
            </filter>
          </defs>
          <rect x="28" y="28" width="200" height="200" rx="40" fill="url(#bgH)" />
          <rect x="28" y="28" width="200" height="200" rx="40" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
          <rect x="28" y="28" width="200" height="94" rx="40" fill="url(#shineH)" />
          <text
            x="128"
            y="128"
            fontSize="124"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(255,255,255,0.22)"
            fontFamily={fontFamily}
            transform="translate(4,4)"
          >
            D
          </text>
          <text
            x="128"
            y="128"
            fontSize="120"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#ffffff"
            fontFamily={fontFamily}
          >
            D
          </text>
          <text
            x="268"
            y="136"
            fontSize="88"
            fontWeight="700"
            textAnchor="start"
            dominantBaseline="middle"
            fill="#ffffff"
            fontFamily={fontFamily}
          >
            DevDriven
          </text>
        </svg>
      )}
    </div>
  );
});

export { Logo };
